import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './Home.css';

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

const Counter = ({ end, suffix = '+', duration = 1600 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const startTime = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - startTime) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              setVal(Math.round(end * eased));
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [end, duration]);

  return (
    <span className="stat-number" ref={ref}>
      {val}
      {suffix}
    </span>
  );
};

const features = [
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect width="40" height="40" rx="10" fill="rgba(25,118,210,0.15)" />
        <rect x="10" y="8" width="20" height="16" rx="2" stroke="#1976D2" strokeWidth="2" fill="none" />
        <line x1="15" y1="28" x2="25" y2="28" stroke="#1976D2" strokeWidth="2" strokeLinecap="round" />
        <line x1="20" y1="24" x2="20" y2="28" stroke="#1976D2" strokeWidth="2" />
      </svg>
    ),
    title: 'TV Remote Control',
    desc: 'Control any smart TV via WiFi or IR blaster. Supports 70+ brands including Samsung, LG, Sony, TCL, and more.',
  },
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect width="40" height="40" rx="10" fill="rgba(124,77,255,0.15)" />
        <path d="M14 12h12a2 2 0 012 2v12a2 2 0 01-2 2H14a2 2 0 01-2-2V14a2 2 0 012-2z" stroke="#7C4DFF" strokeWidth="2" fill="none" />
        <path d="M17 18l3-3 3 3" stroke="#7C4DFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 22l3 3 3-3" stroke="#7C4DFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    title: 'AC Remote Control',
    desc: 'Full AC control with temperature, fan speed, swing, and mode settings. Works with major AC brands.',
  },
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect width="40" height="40" rx="10" fill="rgba(67,160,71,0.15)" />
        <rect x="10" y="10" width="10" height="14" rx="1" stroke="#43A047" strokeWidth="2" fill="none" />
        <rect x="22" y="8" width="10" height="16" rx="2" stroke="#43A047" strokeWidth="2" fill="none" />
        <path d="M20 17h2" stroke="#43A047" strokeWidth="2" strokeLinecap="round" />
        <path d="M15 28l5-4 5 4" stroke="#43A047" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    title: 'Screen Mirroring',
    desc: 'Cast your phone screen to your TV. Share photos, videos, and media files wirelessly.',
  },
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect width="40" height="40" rx="10" fill="rgba(247,141,167,0.15)" />
        <circle cx="20" cy="20" r="8" stroke="#F78DA7" strokeWidth="2" fill="none" />
        <polygon points="18,16 25,20 18,24" fill="#F78DA7" />
      </svg>
    ),
    title: 'IPTV Player',
    desc: 'Browse IPTV playlists from 100+ countries. Import custom playlists and manage your channels.',
  },
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect width="40" height="40" rx="10" fill="rgba(25,118,210,0.15)" />
        <circle cx="20" cy="16" r="4" stroke="#1976D2" strokeWidth="2" fill="none" />
        <path d="M12 30c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke="#1976D2" strokeWidth="2" strokeLinecap="round" fill="none" />
      </svg>
    ),
    title: 'Touchpad Mode',
    desc: 'Navigate your TV with intuitive gesture-based touchpad controls, just like a laptop trackpad.',
  },
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect width="40" height="40" rx="10" fill="rgba(124,77,255,0.15)" />
        <circle cx="16" cy="20" r="6" stroke="#7C4DFF" strokeWidth="2" fill="none" />
        <circle cx="24" cy="20" r="6" stroke="#7C4DFF" strokeWidth="2" fill="none" />
        <path d="M13 15l6 10M21 15l6 10" stroke="#7C4DFF" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    title: '10+ Languages',
    desc: 'Available in English, Hindi, Arabic, Spanish, French, Turkish, Portuguese, German, Italian, and Chinese.',
  },
];

const previewTabs = [
  { id: 'tv', label: 'TV Remote' },
  { id: 'ac', label: 'AC Control' },
  { id: 'iptv', label: 'IPTV Player' },
  { id: 'touchpad', label: 'Touchpad' },
  { id: 'cast', label: 'Screen Mirror' },
];

const iptvChannels = [
  { name: 'Sports HD', tag: 'LIVE', viewers: '12.4K', color: '#E05555' },
  { name: 'News 24', tag: 'LIVE', viewers: '8.1K', color: '#1976D2' },
  { name: 'Movies+', tag: 'HD', viewers: '5.7K', color: '#7C4DFF' },
  { name: 'Kids Zone', tag: 'HD', viewers: '3.2K', color: '#43A047' },
  { name: 'Discovery', tag: 'HD', viewers: '6.9K', color: '#F78DA7' },
  { name: 'Music TV', tag: 'LIVE', viewers: '4.5K', color: '#FFC83D' },
];

const LivePreview = () => {
  const [active, setActive] = useState('tv');
  const [acTemp, setAcTemp] = useState(22);
  const [acMode, setAcMode] = useState('cool');
  const [acFan, setAcFan] = useState('auto');
  const [vol, setVol] = useState(32);
  const [muted, setMuted] = useState(false);
  const [power, setPower] = useState(true);
  const [activeCh, setActiveCh] = useState(0);
  const [cursor, setCursor] = useState({ x: 50, y: 50 });
  const [pressed, setPressed] = useState(null);
  const [castProgress, setCastProgress] = useState(0);

  useEffect(() => {
    if (active !== 'cast') return;
    setCastProgress(0);
    const t = setInterval(() => {
      setCastProgress((p) => (p >= 100 ? 0 : p + 2));
    }, 80);
    return () => clearInterval(t);
  }, [active]);

  useEffect(() => {
    if (active !== 'touchpad') return;
    let raf;
    const start = performance.now();
    const loop = (now) => {
      const t = (now - start) / 1000;
      setCursor({
        x: 50 + Math.sin(t * 0.9) * 28,
        y: 50 + Math.cos(t * 1.3) * 22,
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  const press = (key) => {
    setPressed(key);
    setTimeout(() => setPressed(null), 220);
  };

  return (
    <div className="lp-wrapper reveal reveal-up">
      <div className="lp-tabs" role="tablist">
        {previewTabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={active === t.id}
            className={`lp-tab ${active === t.id ? 'active' : ''}`}
            onClick={() => setActive(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="lp-stage">
        <div className="lp-device">
          <div className="lp-device-notch"></div>
          <div className="lp-device-screen">
            {active === 'tv' && (
              <div className="lp-pane lp-tv">
                <div className="lp-header">
                  <span className={`lp-dot ${power ? 'on' : 'off'}`}></span>
                  {power ? 'Samsung TV · Connected' : 'Standby'}
                </div>
                <button
                  className={`lp-power ${power ? 'on' : ''} ${pressed === 'power' ? 'pressed' : ''}`}
                  onClick={() => { setPower((p) => !p); press('power'); }}
                  aria-label="Power"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M18.36 6.64a9 9 0 11-12.73 0" />
                    <line x1="12" y1="2" x2="12" y2="12" />
                  </svg>
                </button>

                <div className="lp-dpad">
                  {['up','left','ok','right','down'].map((dir) => (
                    <button
                      key={dir}
                      className={`lp-dpad-btn lp-dpad-${dir} ${pressed === dir ? 'pressed' : ''}`}
                      onClick={() => press(dir)}
                      aria-label={dir}
                    >
                      {dir === 'ok' ? 'OK' : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          {dir === 'up' && <path d="M7 14l5-5 5 5z" />}
                          {dir === 'down' && <path d="M7 10l5 5 5-5z" />}
                          {dir === 'left' && <path d="M14 7l-5 5 5 5z" />}
                          {dir === 'right' && <path d="M10 7l5 5-5 5z" />}
                        </svg>
                      )}
                    </button>
                  ))}
                </div>

                <div className="lp-vol-row">
                  <button
                    className="lp-circle-btn"
                    onClick={() => { setVol((v) => Math.max(0, v - 4)); press('vol-'); }}
                    aria-label="Volume down"
                  >−</button>
                  <div className="lp-vol-bar">
                    <div className="lp-vol-fill" style={{ width: `${muted ? 0 : vol}%` }}></div>
                    <span className="lp-vol-label">{muted ? 'Muted' : `Vol ${vol}`}</span>
                  </div>
                  <button
                    className="lp-circle-btn"
                    onClick={() => { setVol((v) => Math.min(100, v + 4)); setMuted(false); press('vol+'); }}
                    aria-label="Volume up"
                  >+</button>
                </div>

                <div className="lp-actions">
                  <button className={`lp-chip ${muted ? 'chip-danger' : ''}`} onClick={() => setMuted((m) => !m)}>
                    {muted ? 'Unmute' : 'Mute'}
                  </button>
                  <button className="lp-chip">Home</button>
                  <button className="lp-chip">Input</button>
                </div>
              </div>
            )}

            {active === 'ac' && (
              <div className="lp-pane lp-ac">
                <div className="lp-header">
                  <span className="lp-dot on"></span>
                  Living Room AC · {acMode.toUpperCase()}
                </div>
                <div className="lp-ac-dial">
                  <svg viewBox="0 0 160 160" className="lp-ac-ring">
                    <circle cx="80" cy="80" r="68" stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
                    <circle
                      cx="80" cy="80" r="68"
                      stroke="url(#acGrad)"
                      strokeWidth="10"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${((acTemp - 16) / 14) * 427} 427`}
                      transform="rotate(-90 80 80)"
                      style={{ transition: 'stroke-dasharray 0.4s ease' }}
                    />
                    <defs>
                      <linearGradient id="acGrad" x1="0" y1="0" x2="160" y2="160">
                        <stop offset="0%" stopColor="#1976D2" />
                        <stop offset="100%" stopColor="#7C4DFF" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="lp-ac-value">
                    <span className="lp-ac-temp">{acTemp}</span>
                    <span className="lp-ac-unit">°C</span>
                  </div>
                  <div className="lp-ac-controls">
                    <button className="lp-circle-btn" onClick={() => setAcTemp((t) => Math.max(16, t - 1))}>−</button>
                    <button className="lp-circle-btn" onClick={() => setAcTemp((t) => Math.min(30, t + 1))}>+</button>
                  </div>
                </div>
                <div className="lp-ac-row">
                  <div className="lp-ac-group">
                    <span className="lp-ac-label">Mode</span>
                    <div className="lp-seg">
                      {['cool','heat','dry','fan'].map((m) => (
                        <button key={m} className={`lp-seg-btn ${acMode === m ? 'active' : ''}`} onClick={() => setAcMode(m)}>{m}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="lp-ac-row">
                  <div className="lp-ac-group">
                    <span className="lp-ac-label">Fan</span>
                    <div className="lp-seg">
                      {['auto','low','med','high'].map((f) => (
                        <button key={f} className={`lp-seg-btn ${acFan === f ? 'active' : ''}`} onClick={() => setAcFan(f)}>{f}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {active === 'iptv' && (
              <div className="lp-pane lp-iptv">
                <div className="lp-header">
                  <span className="lp-dot on"></span>
                  IPTV · 124 Channels
                </div>
                <div className="lp-iptv-now">
                  <div className="lp-iptv-thumb" style={{ background: `linear-gradient(135deg, ${iptvChannels[activeCh].color}, #111)` }}>
                    <span className="lp-iptv-live">● {iptvChannels[activeCh].tag}</span>
                    <span className="lp-iptv-title">{iptvChannels[activeCh].name}</span>
                  </div>
                  <div className="lp-iptv-meta">
                    <span>{iptvChannels[activeCh].viewers} watching</span>
                  </div>
                </div>
                <div className="lp-iptv-list">
                  {iptvChannels.map((c, i) => (
                    <button
                      key={i}
                      className={`lp-iptv-item ${i === activeCh ? 'active' : ''}`}
                      onClick={() => setActiveCh(i)}
                    >
                      <span className="lp-iptv-swatch" style={{ background: c.color }}></span>
                      <span className="lp-iptv-name">{c.name}</span>
                      <span className="lp-iptv-tag">{c.tag}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {active === 'touchpad' && (
              <div className="lp-pane lp-touchpad">
                <div className="lp-header">
                  <span className="lp-dot on"></span>
                  Gesture Touchpad
                </div>
                <div className="lp-trackpad">
                  <div className="lp-trackpad-grid"></div>
                  <span
                    className="lp-cursor"
                    style={{ left: `${cursor.x}%`, top: `${cursor.y}%` }}
                  ></span>
                  <span className="lp-trackpad-hint">Swipe · Tap · Pinch</span>
                </div>
                <div className="lp-touch-actions">
                  <button className="lp-chip">Back</button>
                  <button className="lp-chip">Home</button>
                  <button className="lp-chip">Keyboard</button>
                </div>
              </div>
            )}

            {active === 'cast' && (
              <div className="lp-pane lp-cast">
                <div className="lp-header">
                  <span className="lp-dot on"></span>
                  Casting to Living Room TV
                </div>
                <div className="lp-cast-visual">
                  <div className="lp-cast-phone">
                    <div className="lp-cast-phone-screen"></div>
                  </div>
                  <div className="lp-cast-waves">
                    <span></span><span></span><span></span>
                  </div>
                  <div className="lp-cast-tv">
                    <div className="lp-cast-tv-screen">
                      <div className="lp-cast-stream" style={{ transform: `translateX(${castProgress - 50}%)` }}></div>
                    </div>
                    <div className="lp-cast-tv-stand"></div>
                  </div>
                </div>
                <div className="lp-cast-progress">
                  <div className="lp-cast-progress-fill" style={{ width: `${castProgress}%` }}></div>
                </div>
                <div className="lp-cast-meta">
                  <span>1080p · 60 FPS</span>
                  <span>{castProgress}%</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const brands = [
  'Samsung', 'LG', 'Sony', 'TCL', 'Philips',
  'Panasonic', 'Sharp', 'Toshiba', 'Hisense', 'Vizio',
  'Haier', 'Xiaomi',
];

const Home = () => {
  useReveal();
  const heroRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const onMove = (e) => {
      const rect = hero.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      hero.style.setProperty('--mx', x.toFixed(3));
      hero.style.setProperty('--my', y.toFixed(3));
    };
    hero.addEventListener('mousemove', onMove);
    return () => hero.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className="home">
      <SEO
        title="Remotix: Universal TV Remote – Control Any TV, AC & More From Your Phone"
        description="Free universal remote app for 70+ TV brands. Control TV, AC, cast your screen and stream IPTV — all from your phone. Download for Android & iOS."
      />
      {/* Hero Section */}
      <section className="hero" ref={heroRef}>
        <div className="hero-bg">
          <div className="hero-glow hero-glow-1"></div>
          <div className="hero-glow hero-glow-2"></div>
          <div className="hero-particles">
            {Array.from({ length: 18 }).map((_, i) => (
              <span
                key={i}
                className={`particle particle-${i % 6}`}
                style={{
                  '--i': i,
                  '--x': `${(i * 53) % 100}%`,
                  '--y': `${(i * 37) % 100}%`,
                }}
              />
            ))}
          </div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge reveal reveal-fade">
              <span className="badge-dot"></span>
              Smart Remote Control App
            </div>
            <h1 className="reveal reveal-up">
              Control Your TV
              <span className="gradient-text gradient-animate"> From Anywhere</span>
            </h1>
            <p className="hero-desc reveal reveal-up reveal-d1">
              Turn your smartphone into a universal remote. Control TVs, ACs, cast your screen,
              and stream IPTV channels - all from one powerful app.
            </p>
            <div className="hero-actions reveal reveal-up reveal-d2">
              <a href="https://play.google.com/store/apps/details?id=com.remote.master.universal" target="_blank" rel="noopener noreferrer" className="store-btn android">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92z" fill="#4285F4"/>
                  <path d="M17.556 8.236L5.178.904a1.003 1.003 0 00-1.07.018l9.89 9.89 3.558-2.576z" fill="#34A853"/>
                  <path d="M17.556 15.764l-3.558-2.576-9.89 9.89a1.003 1.003 0 001.07.018l12.378-7.332z" fill="#FBBC04"/>
                  <path d="M20.778 10.906l-3.222-1.91-3.764 2.724 3.764 2.724 3.222-1.91a1.19 1.19 0 000-1.628z" fill="#EA4335"/>
                </svg>
                <div className="store-btn-text">
                  <span className="store-btn-small">GET IT ON</span>
                  <span className="store-btn-big">Google Play</span>
                </div>
              </a>
              <a href="https://apps.apple.com/app/id6760695054" target="_blank" rel="noopener noreferrer" className="store-btn ios">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="store-btn-text">
                  <span className="store-btn-small">Download on the</span>
                  <span className="store-btn-big">App Store</span>
                </div>
              </a>
            </div>
            <div className="hero-stats reveal reveal-up reveal-d3">
              <div className="stat">
                <Counter end={70} />
                <span className="stat-label">TV Brands</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <Counter end={100} />
                <span className="stat-label">Countries</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <Counter end={10} />
                <span className="stat-label">Languages</span>
              </div>
            </div>
          </div>

          <div className="hero-visual reveal reveal-zoom reveal-d2">
            <div className="phone-mockup phone-float">
              <div className="phone-glow-ring"></div>
              <div className="phone-frame">
                <div className="phone-notch"></div>
                <div className="phone-screen">
                  <div className="remote-ui">
                    {/* Status Bar */}
                    <div className="remote-status-bar">
                      <span>9:41</span>
                      <div className="status-icons">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#888"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.237 4.237 0 00-6 0zm-4-4l2 2a7.074 7.074 0 0110 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#888"><rect x="2" y="6" width="18" height="12" rx="2" stroke="#888" strokeWidth="2" fill="none"/><rect x="22" y="10" width="2" height="4" rx="1" fill="#888"/><rect x="5" y="9" width="10" height="6" rx="1" fill="#888"/></svg>
                      </div>
                    </div>

                    {/* Connected Device */}
                    <div className="remote-device-info">
                      <div className="device-dot"></div>
                      <span>Samsung TV - Living Room</span>
                    </div>

                    {/* Power Button */}
                    <button className="remote-power-btn">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M18.36 6.64a9 9 0 11-12.73 0"/>
                        <line x1="12" y1="2" x2="12" y2="12"/>
                      </svg>
                    </button>

                    {/* D-Pad */}
                    <div className="remote-dpad-ring">
                      <div className="dpad-arrow dpad-up">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M7 14l5-5 5 5z"/></svg>
                      </div>
                      <div className="dpad-arrow dpad-right">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M10 7l5 5-5 5z"/></svg>
                      </div>
                      <div className="dpad-arrow dpad-down">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M7 10l5 5 5-5z"/></svg>
                      </div>
                      <div className="dpad-arrow dpad-left">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M14 7l-5 5 5 5z"/></svg>
                      </div>
                      <div className="dpad-center">OK</div>
                    </div>

                    {/* Vol / Ch Controls */}
                    <div className="remote-vol-ch">
                      <div className="vol-ch-group">
                        <div className="vol-ch-label">VOL</div>
                        <div className="vol-ch-btns">
                          <div className="vol-ch-btn">+</div>
                          <div className="vol-ch-btn">-</div>
                        </div>
                      </div>
                      <button className="remote-mute-btn">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                          <line x1="23" y1="9" x2="17" y2="15"/>
                          <line x1="17" y1="9" x2="23" y2="15"/>
                        </svg>
                      </button>
                      <div className="vol-ch-group">
                        <div className="vol-ch-label">CH</div>
                        <div className="vol-ch-btns">
                          <div className="vol-ch-btn">+</div>
                          <div className="vol-ch-btn">-</div>
                        </div>
                      </div>
                    </div>

                    {/* Media Controls */}
                    <div className="remote-media">
                      <div className="media-btn">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="#aaa"><path d="M19 12L5 5v14z" transform="rotate(180 12 12)"/><rect x="3" y="5" width="3" height="14" fill="#aaa"/></svg>
                      </div>
                      <div className="media-btn media-play">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                      </div>
                      <div className="media-btn">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="#aaa"><path d="M5 12l14-7v14z"/><rect x="18" y="5" width="3" height="14" fill="#aaa"/></svg>
                      </div>
                    </div>

                    {/* Color Buttons */}
                    <div className="remote-colors">
                      <div className="color-btn color-red"></div>
                      <div className="color-btn color-green"></div>
                      <div className="color-btn color-yellow"></div>
                      <div className="color-btn color-blue"></div>
                    </div>

                    {/* App Quick Launch */}
                    <div className="remote-apps">
                      <div className="app-btn app-yt">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M23.5 6.2a3.02 3.02 0 00-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.56A3.02 3.02 0 00.5 6.2 31.6 31.6 0 000 12a31.6 31.6 0 00.5 5.8 3.02 3.02 0 002.12 2.14c1.87.56 9.38.56 9.38.56s7.5 0 9.38-.56a3.02 3.02 0 002.12-2.14A31.6 31.6 0 0024 12a31.6 31.6 0 00-.5-5.8zM9.55 15.5V8.5l6.27 3.5-6.27 3.5z"/></svg>
                      </div>
                      <div className="app-btn app-nf">
                        <span>N</span>
                      </div>
                      <div className="app-btn app-home">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="section-container">
          <div className="section-header reveal reveal-up">
            <span className="section-badge">Features</span>
            <h2>Everything You Need in <span className="gradient-text gradient-animate">One App</span></h2>
            <p>A complete remote control solution for all your home entertainment devices.</p>
          </div>
          <div className="features-grid">
            {features.map((f, i) => (
              <div
                className="feature-card reveal reveal-up"
                key={i}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="feature-card-shine"></div>
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Previews Section */}
      <section className="live-previews">
        <div className="section-container">
          <div className="section-header reveal reveal-up">
            <span className="section-badge">Live Previews</span>
            <h2>See the App <span className="gradient-text gradient-animate">in Action</span></h2>
            <p>Interact with real previews of our most-loved features.</p>
          </div>
          <LivePreview />
        </div>
      </section>

      {/* Brands Section */}
      <section className="brands">
        <div className="section-container">
          <div className="section-header reveal reveal-up">
            <span className="section-badge">Compatibility</span>
            <h2>Works With <span className="gradient-text gradient-animate">Your TV</span></h2>
            <p>Compatible with all major TV brands worldwide.</p>
          </div>
          <div className="brands-grid">
            {brands.map((brand, i) => (
              <div
                className="brand-card reveal reveal-up"
                key={i}
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <span>{brand}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="section-container">
          <div className="section-header reveal reveal-up">
            <span className="section-badge">How It Works</span>
            <h2>Get Started in <span className="gradient-text gradient-animate">3 Steps</span></h2>
          </div>
          <div className="steps-grid">
            <div className="step-card reveal reveal-up">
              <div className="step-number">1</div>
              <h3>Download the App</h3>
              <p>Install Remotix: Universal TV Remote from the Google Play Store or Apple App Store for free.</p>
            </div>
            <div className="step-connector reveal reveal-grow reveal-d1"></div>
            <div className="step-card reveal reveal-up reveal-d1">
              <div className="step-number">2</div>
              <h3>Select Your Device</h3>
              <p>Choose your TV or AC brand from our extensive list, or auto-discover devices on your WiFi network.</p>
            </div>
            <div className="step-connector reveal reveal-grow reveal-d2"></div>
            <div className="step-card reveal reveal-up reveal-d2">
              <div className="step-number">3</div>
              <h3>Start Controlling</h3>
              <p>Use the intuitive remote interface to control your device instantly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="cta-container">
          <div className="cta-bg">
            <div className="cta-glow"></div>
            <div className="cta-orb cta-orb-1"></div>
            <div className="cta-orb cta-orb-2"></div>
            <div className="cta-orb cta-orb-3"></div>
          </div>
          <div className="cta-content reveal reveal-zoom">
            <div className="cta-border-glow"></div>
            <span className="cta-sparkle cta-sparkle-1"></span>
            <span className="cta-sparkle cta-sparkle-2"></span>
            <span className="cta-sparkle cta-sparkle-3"></span>

            <div className="cta-badge">
              <span className="cta-badge-dot"></span>
              Free Download · No Sign-up
            </div>

            <h2>Ready to Control Your <span className="gradient-text gradient-animate">TV?</span></h2>
            <p>Download Remotix: Universal TV Remote now and transform your smartphone into a powerful remote control.</p>

            <div className="cta-rating">
              <div className="cta-stars" aria-label="4.8 out of 5 stars">
                {[0,1,2,3,4].map((i) => (
                  <svg key={i} width="18" height="18" viewBox="0 0 24 24" fill="#FFC83D" style={{ animationDelay: `${i * 120}ms` }} className="cta-star">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </div>
              <span className="cta-rating-text"><strong>4.8</strong> · 10K+ happy users</span>
            </div>

            <div className="cta-buttons">
              <a href="https://play.google.com/store/apps/details?id=com.remote.master.universal" target="_blank" rel="noopener noreferrer" className="store-btn android">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92z" fill="#4285F4"/>
                  <path d="M17.556 8.236L5.178.904a1.003 1.003 0 00-1.07.018l9.89 9.89 3.558-2.576z" fill="#34A853"/>
                  <path d="M17.556 15.764l-3.558-2.576-9.89 9.89a1.003 1.003 0 001.07.018l12.378-7.332z" fill="#FBBC04"/>
                  <path d="M20.778 10.906l-3.222-1.91-3.764 2.724 3.764 2.724 3.222-1.91a1.19 1.19 0 000-1.628z" fill="#EA4335"/>
                </svg>
                <div className="store-btn-text">
                  <span className="store-btn-small">GET IT ON</span>
                  <span className="store-btn-big">Google Play</span>
                </div>
              </a>
              <a href="https://apps.apple.com/app/id6760695054" target="_blank" rel="noopener noreferrer" className="store-btn ios">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="store-btn-text">
                  <span className="store-btn-small">Download on the</span>
                  <span className="store-btn-big">App Store</span>
                </div>
              </a>
            </div>

            <ul className="cta-trust">
              <li>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#43A047" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                100% Free
              </li>
              <li>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#43A047" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                No Ads Tracking
              </li>
              <li>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#43A047" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                Works Offline (IR)
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
